import { O as ObjectMap, _ as _slicedToArray, h as has, d as deepEqual, a as _objectSpread2, c as combineRawFlagsWithDefaultFlags, s as serializeVisitorKeyCookie, g as getCookie } from '../../dist/utils-a718e420.esm.js';
import * as React from 'react';
import { nanoid } from 'nanoid';
import { i as isConfigured, c as config, M as MissingConfigurationError } from '../../dist/config-9cc484a4.esm.js';

var getId = function () {
  var id = 0;
  return function () {
    return id++;
  };
}();

/**
 * Returns new effects and pending state, cancels controller of previous pending
 * state.
 *
 * @param input Next input
 * @param pending Previous pending state
 * @returns [Effects to execute, Next pending state]
 */
function createFetchEffects(input, pending) {
  var id = getId();
  var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  var fetchEffect = {
    effect: "fetch",
    id: id,
    controller: controller,
    input: input
  };
  if (pending !== null && pending !== void 0 && pending.controller) pending.controller.abort();
  return [[fetchEffect], {
    id: id,
    controller: controller
  }];
}

function canSettle(state) {
  return state.name === "evaluating" || state.name === "revalidating-after-success" || state.name === "revalidating-after-failure";
}
/**
 * The reducer returns a tuple of [state, effects].
 *
 * effects is an array of effects to execute. The emitted effects are then later
 * executed in another hook.
 *
 * This pattern is basically a hand-rolled version of
 * https://github.com/davidkpiano/useEffectReducer
 *
 * We use a hand-rolled version to keep the size of this package minimal.
 */


function reducer(tuple, action) {
  var _tuple = _slicedToArray(tuple, 1),
      state
  /* and effects */
  = _tuple[0];

  switch (action.type) {
    case "evaluate":
      {
        var cachedOutcome = cache.get(action.input);

        var _createFetchEffects = createFetchEffects(action.input, state.pending),
            _createFetchEffects2 = _slicedToArray(_createFetchEffects, 2),
            effects = _createFetchEffects2[0],
            pending = _createFetchEffects2[1]; // action.input will always differ from state.input, because we do not
        // dispatch "evaluate" otherwise


        return [{
          name: "evaluating",
          input: action.input,
          cachedOutcome: cachedOutcome,
          pending: pending
        }, effects];
      }

    case "revalidate":
      {
        if (state.name === "empty") return tuple;
        var input = action.input || state.input;

        var _createFetchEffects3 = createFetchEffects(input, state.pending),
            _createFetchEffects4 = _slicedToArray(_createFetchEffects3, 2),
            _effects = _createFetchEffects4[0],
            _pending = _createFetchEffects4[1];

        if (state.name === "succeeded") return [{
          name: "revalidating-after-success",
          input: state.input,
          outcome: state.outcome,
          cachedOutcome: state.cachedOutcome,
          pending: _pending
        }, _effects];
        if (state.name === "failed") return [{
          name: "revalidating-after-failure",
          input: state.input,
          outcome: state.outcome,
          cachedOutcome: state.cachedOutcome,
          pending: _pending
        }, _effects];
        if (state.name === "evaluating") return [{
          name: "evaluating",
          input: state.input,
          outcome: state.outcome,
          cachedOutcome: state.cachedOutcome,
          pending: _pending
        }, _effects];
        return tuple;
      }

    case "settle/failure":
      {
        var _state$pending;

        if (!canSettle(state)) return tuple; // ignore outdated responses

        if (((_state$pending = state.pending) === null || _state$pending === void 0 ? void 0 : _state$pending.id) !== action.id) return tuple;

        if (action.thrownError) {
          console.error("HappyKit: Failed to load flags");
          console.error(action.thrownError);
        }

        var _cachedOutcome = cache.get(action.input);

        return [{
          name: "failed",
          input: action.input,
          outcome: action.outcome,
          cachedOutcome: _cachedOutcome
        }, []];
      }

    case "settle/success":
      {
        var _state$pending2, _action$outcome$data$;

        if (!canSettle(state)) return tuple; // ignore outdated responses

        if (((_state$pending2 = state.pending) === null || _state$pending2 === void 0 ? void 0 : _state$pending2.id) !== action.id) return tuple;
        var visitorKey = (_action$outcome$data$ = action.outcome.data.visitor) === null || _action$outcome$data$ === void 0 ? void 0 : _action$outcome$data$.key;
        if (visitorKey) document.cookie = serializeVisitorKeyCookie(visitorKey);
        cache.set(action.input, action.outcome);
        return [{
          name: "succeeded",
          input: action.input,
          outcome: action.outcome
        }, []];
      }

    default:
      return tuple;
  }
}

function getInput(_ref) {
  var config = _ref.config,
      visitorKeyInState = _ref.visitorKeyInState,
      generatedVisitorKey = _ref.generatedVisitorKey,
      user = _ref.user,
      traits = _ref.traits;
  var cookie = typeof document !== "undefined" ? getCookie(document.cookie, "hkvk") : null;
  return {
    endpoint: config.endpoint,
    envKey: config.envKey,
    requestBody: {
      visitorKey: cookie || visitorKeyInState || generatedVisitorKey,
      user: user,
      traits: traits,
      "static": false
    }
  };
}

function omitStaticDifferences(input) {
  return _objectSpread2(_objectSpread2({}, input), {}, {
    requestBody: _objectSpread2(_objectSpread2({}, input.requestBody), {}, {
      "static": null,
      visitorKey: null
    })
  });
}
/**
 * Returns true if the inputs are exactly equal...
 *
 * Or in case the current input is static, returns true when they are the same
 * except for the static properties ("static" and "visitorKey").
 */


function isAlmostEqual(currentInput, nextInput) {
  // special treatment when the current input is static
  // in this case, we ignore the static and visitorKey properties
  return currentInput !== null && currentInput !== void 0 && currentInput.requestBody["static"] ? deepEqual(omitStaticDifferences(currentInput), omitStaticDifferences(nextInput)) : deepEqual(currentInput, nextInput);
}

var cache = new ObjectMap();
function useFlags() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!isConfigured(config)) throw new MissingConfigurationError();
  var staticConfig = config;

  var _React$useState = React.useState(nanoid),
      _React$useState2 = _slicedToArray(_React$useState, 1),
      generatedVisitorKey = _React$useState2[0];

  var currentUser = options.user || null;
  var currentTraits = options.traits || null;
  var shouldRevalidateOnFocus = options.revalidateOnFocus === undefined ? config.revalidateOnFocus : options.revalidateOnFocus;
  var currentLoadingTimeout = has(options, "loadingTimeout") ? options.loadingTimeout : config.loadingTimeout || 0;

  var _React$useReducer = React.useReducer(reducer, options.initialState, function (initialFlagState) {
    if (!(initialFlagState !== null && initialFlagState !== void 0 && initialFlagState.input)) return [{
      name: "empty"
    }, []];
    var input = getInput({
      config: staticConfig,
      visitorKeyInState: initialFlagState.input.requestBody.visitorKey,
      generatedVisitorKey: generatedVisitorKey,
      user: currentUser,
      traits: currentTraits
    });
    if (initialFlagState.outcome.error) return [{
      name: "failed",
      input: initialFlagState.input,
      outcome: initialFlagState.outcome,
      cachedOutcome: cache.get(initialFlagState.input)
    }, // always revalidate because the initial state failed
    [{
      effect: "revalidate-input",
      input: input
    }]];
    cache.set(initialFlagState.input, initialFlagState.outcome);
    return [{
      name: "succeeded",
      input: initialFlagState.input,
      outcome: initialFlagState.outcome
    }, // revalidate only if the initial state was for a static render
    initialFlagState.input.requestBody["static"] ? [{
      effect: "revalidate-input",
      input: input
    }] : []];
  }),
      _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
      _React$useReducer2$ = _slicedToArray(_React$useReducer2[0], 2),
      state = _React$useReducer2$[0],
      effects = _React$useReducer2$[1],
      dispatch = _React$useReducer2[1];

  React.useEffect(function () {
    var _state$input;

    var input = getInput({
      config: staticConfig,
      visitorKeyInState: (_state$input = state.input) === null || _state$input === void 0 ? void 0 : _state$input.requestBody.visitorKey,
      generatedVisitorKey: generatedVisitorKey,
      user: currentUser,
      traits: currentTraits
    }); // evaluate if the input has changed, but not if the current input is
    // static as that will be revalidated on initialisation

    if (!options.pause && !isAlmostEqual(state.input, input)) {
      dispatch({
        type: "evaluate",
        input: input
      });
    }

    if (!shouldRevalidateOnFocus) return;

    function handleFocus() {
      if (document.visibilityState === "visible" && !options.pause) {
        dispatch({
          type: "revalidate"
        });
      }
    } // extracted "visibilitychange" for bundle size


    var visibilityChange = "visibilitychange";
    document.addEventListener(visibilityChange, handleFocus);
    return function () {
      document.removeEventListener(visibilityChange, handleFocus);
    };
  }, [state, currentUser, currentTraits, shouldRevalidateOnFocus, options.pause]);
  var revalidate = React.useCallback(function () {
    return dispatch({
      type: "revalidate"
    });
  }, [dispatch]);
  React.useEffect(function () {
    effects.forEach(function (effect) {
      switch (effect.effect) {
        // execute the effect
        case "fetch":
          {
            var id = effect.id,
                input = effect.input,
                controller = effect.controller;
            var timeoutId;

            if (controller && currentLoadingTimeout) {
              timeoutId = setTimeout(function () {
                return controller.abort();
              }, currentLoadingTimeout);
            }

            fetch([input.endpoint, input.envKey].join("/"), {
              method: "POST",
              headers: {
                "content-type": "application/json"
              },
              body: JSON.stringify(input.requestBody),
              signal: controller === null || controller === void 0 ? void 0 : controller.signal
            }).then(function (response) {
              clearTimeout(timeoutId);

              if (!response.ok
              /* response.status is not 200-299 */
              ) {
                  dispatch({
                    type: "settle/failure",
                    id: id,
                    input: input,
                    outcome: {
                      error: "response-not-ok"
                    },
                    thrownError: new Error("Response not ok")
                  });
                  return null;
                }

              return response.json().then(function (data) {
                // responses to outdated requests are skipped in the reducer
                dispatch({
                  type: "settle/success",
                  id: id,
                  input: input,
                  outcome: {
                    data: data
                  }
                });
              }, function (thrownError) {
                dispatch({
                  type: "settle/failure",
                  id: id,
                  input: input,
                  outcome: {
                    error: "invalid-response-body"
                  },
                  thrownError: thrownError
                });
                return null;
              });
            }, function (error) {
              // aborted from controller due to timeout
              if (error instanceof DOMException && error.name === "AbortError") {
                dispatch({
                  type: "settle/failure",
                  id: id,
                  input: input,
                  outcome: {
                    error: "request-timed-out"
                  },
                  thrownError: error
                });
              } else {
                dispatch({
                  type: "settle/failure",
                  id: id,
                  input: input,
                  outcome: {
                    error: "network-error"
                  },
                  thrownError: error
                });
              }

              return null;
            });
            break;
          }

        case "revalidate-input":
          dispatch({
            type: "revalidate",
            input: effect.input
          });
          break;

        default:
          return;
      }
    });
  }, [effects, dispatch]);
  var defaultFlags = config.defaultFlags;
  var flagBag = React.useMemo(function () {
    var _state$cachedOutcome, _state$cachedOutcome2, _state$cachedOutcome3;

    switch (state.name) {
      case "evaluating":
        return {
          flags: (_state$cachedOutcome = state.cachedOutcome) !== null && _state$cachedOutcome !== void 0 && _state$cachedOutcome.data.flags ? combineRawFlagsWithDefaultFlags(state.cachedOutcome.data.flags, defaultFlags) : null,
          data: null,
          error: null,
          fetching: true,
          settled: false,
          revalidate: revalidate,
          visitorKey: state.input.requestBody.visitorKey
        };

      case "succeeded":
        return {
          flags: combineRawFlagsWithDefaultFlags(state.outcome.data.flags, defaultFlags),
          data: state.outcome.data,
          error: null,
          fetching: false,
          settled: !state.input.requestBody["static"],
          revalidate: revalidate,
          visitorKey: state.input.requestBody.visitorKey
        };

      case "revalidating-after-success":
        return {
          flags: combineRawFlagsWithDefaultFlags(state.outcome.data.flags, defaultFlags),
          data: state.outcome.data,
          error: null,
          fetching: true,
          settled: !state.input.requestBody["static"],
          revalidate: revalidate,
          visitorKey: state.input.requestBody.visitorKey
        };

      case "failed":
        return {
          flags: (_state$cachedOutcome2 = state.cachedOutcome) !== null && _state$cachedOutcome2 !== void 0 && _state$cachedOutcome2.data.flags ? combineRawFlagsWithDefaultFlags(state.cachedOutcome.data.flags, defaultFlags) : defaultFlags || null,
          data: null,
          error: state.outcome.error,
          fetching: false,
          settled: !state.input.requestBody["static"],
          revalidate: revalidate,
          visitorKey: state.input.requestBody.visitorKey
        };

      case "revalidating-after-failure":
        return {
          flags: (_state$cachedOutcome3 = state.cachedOutcome) !== null && _state$cachedOutcome3 !== void 0 && _state$cachedOutcome3.data.flags ? combineRawFlagsWithDefaultFlags(state.cachedOutcome.data.flags, defaultFlags) : defaultFlags || null,
          data: null,
          error: state.outcome.error,
          fetching: true,
          settled: !state.input.requestBody["static"],
          revalidate: revalidate,
          visitorKey: state.input.requestBody.visitorKey
        };

      default:
      case "empty":
        return {
          flags: null,
          data: null,
          error: null,
          fetching: false,
          settled: false,
          revalidate: revalidate,
          visitorKey: null
        };
    }
  }, [state, defaultFlags, revalidate]);
  return flagBag;
}

export { cache, useFlags };
